/**
 * 七牛云上传工具类
 * 用于手写签名图片上传
 */

import SparkMD5 from 'spark-md5'

class QiniuUploader {
  constructor (config) {
    this.config = config || {}
  }

  /**
   * 上传文件到七牛云
   * @param {File} file - 要上传的文件
   * @param {Object} options - 上传选项
   * @returns {Promise}
   */
  async uploadFile (file, options = {}) {
    try {
      // 计算文件MD5
      const md5 = await this.calculateFileMD5(file)

      // 构建FormData
      const formData = this.buildFormData(file, md5, options)

      // 发送上传请求
      const response = await this.sendUploadRequest(formData)

      return response
    } catch (error) {
      console.error('上传文件失败:', error)
      throw error
    }
  }

  /**
   * 将Base64转换为File对象
   * @param {String} base64 - Base64字符串
   * @param {String} fileName - 文件名
   * @returns {File}
   */
  base64ToFile (base64, fileName) {
    const arr = base64.split(',')
    const mime = arr[0].match(/:(.*?);/)[1]
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }

    return new File([u8arr], fileName, { type: mime })
  }

  /**
   * 计算文件MD5
   * @param {File} file
   * @returns {Promise<string>}
   */
  calculateFileMD5 (file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = () => {
        const spark = new SparkMD5.ArrayBuffer()
        spark.append(reader.result)
        const hash = spark.end()
        resolve(hash)
      }

      reader.onerror = () => {
        reject(new Error('Error reading file'))
      }

      reader.readAsArrayBuffer(file)
    })
  }

  /**
   * 构建上传FormData
   * @param {File} file
   * @param {String} md5
   * @param {Object} options
   * @returns {FormData}
   */
  buildFormData (file, md5, options) {
    const formData = new FormData()

    // 文件路径key，格式：cf/xxx.png
    const key = `cf/${file.name}`

    formData.append('key', key)
    formData.append('x:team_id', this.config.en_team_id)
    formData.append('x:object_id', this.config.en_project_id || this.config.en_team_id)
    formData.append('x:sub_object_id', options.subObjectId || '')
    formData.append('x:object_type', options.objectType || 'activity')
    formData.append('x:md5', md5)
    formData.append('token', this.config.qiniu_token)
    formData.append('file', file)

    return formData
  }

  /**
   * 发送上传请求
   * @param {FormData} formData
   * @returns {Promise}
   */
  sendUploadRequest (formData) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.addEventListener('load', () => {
        if (xhr.status !== 200) {
          reject(new Error(`上传失败: ${xhr.status}`))
          return
        }

        const response = JSON.parse(xhr.responseText)
        if (response.error !== 0) {
          reject(new Error(response.message || '上传失败'))
          return
        }

        resolve(response)
      })

      xhr.addEventListener('error', () => {
        reject(new Error('网络错误'))
      })

      xhr.addEventListener('timeout', () => {
        reject(new Error('上传超时'))
      })

      xhr.timeout = 60000 // 60秒超时

      // 七牛上传地址固定为 https://up.qbox.me/
      xhr.open('POST', 'https://up.qbox.me/', true)
      xhr.send(formData)
    })
  }

  /**
   * 生成随机字符串
   * @param {Number} len - 长度
   * @returns {String}
   */
  static randomString (len = 32) {
    const chars = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678-oOLl9gqVvUuI1'
    const maxPos = chars.length
    let pwd = ''
    for (let i = 0; i < len; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * maxPos))
    }
    return pwd
  }
}

export default QiniuUploader




// WEBPACK FOOTER //
// ./src/utils/qiniuUploader.js